import React, { useEffect, useState } from "react";
import { CopilotAvatar } from "./CopilotMark";

/* The wait.
 *
 * A tool call takes seconds and a multi-step turn takes tens of them. A single
 * frozen "Thinking…" through that reads as a hang, so people reload and lose the
 * turn. Reasoning UIs solved this by showing the work: a clock that proves time
 * is passing, and named steps that prove the time is buying something.
 *
 * Every NAMED step here is driven by a real server frame. Before the first one
 * arrives we do not know what is happening, and the opening state says so: it
 * is present and moving, but its label claims nothing beyond "working" and its
 * bar is indeterminate.
 *
 * That line is where the discipline sits. Filling the gap with invented copy —
 * "Analysing your documents…" before anything has been analysed — asserts work
 * that may not be happening, and a bar with a percentage implies a proportion
 * nothing measured. A fake trace is worse than a spinner, because people
 * believe it. Presence and motion are free to be as rich as you like; only the
 * claims have to be earned.
 */

const TOOL_LABEL = {
  list_projects: "Looking through your projects",
  get_project_status: "Checking project status",
  switch_project: "Switching project",
  search_documents: "Searching documents",
  search_company_knowledge_base: "Searching the knowledge base",
  search_web: "Searching the web",
  create_project: "Creating the project",
  upload_documents: "Adding the documents",
  find_tender: "Looking up the tender",
  recommend_tenders: "Finding tenders that fit you",
  tender_strategy: "Analysing your Memory against Tender Search",
  import_tender: "Creating the project from the tender",
};

/* Past tense, for the collapsed summary. "Thought for 5 seconds" is what a
 * chatbot says when it has nothing better to report; when we know the work was
 * creating a project, saying so costs the same space and actually tells the
 * user what those 5 seconds bought. */
const TOOL_LABEL_DONE = {
  list_projects: "Looked through your projects",
  get_project_status: "Checked project status",
  switch_project: "Switched project",
  search_documents: "Searched documents",
  search_company_knowledge_base: "Searched the knowledge base",
  search_web: "Searched the web",
  create_project: "Created the project",
  upload_documents: "Added the documents",
  find_tender: "Looked up the tender",
  recommend_tenders: "Found tenders that fit you",
  tender_strategy: "Analysed your Memory against Tender Search",
  import_tender: "Created the project from the tender",
};

const labelFor = (name) =>
  TOOL_LABEL[name] || String(name || "Working").replace(/_/g, " ");

const doneLabelFor = (name) =>
  TOOL_LABEL_DONE[name] || TOOL_LABEL[name] || String(name || "Worked").replace(/_/g, " ");

/* What the panel is doing RIGHT NOW.
 *
 * A fixed "Thinking" is the one label guaranteed to be wrong most of the time —
 * the agent is searching, or creating something, and the header is the one line
 * read at a glance. Track the running step; fall back only when no step is
 * live, which is the brief gap where the model is composing its reply.
 */
const headlineFor = (steps) => {
  const running = [...steps].reverse().find((s) => s.status === "running");
  if (running) return labelFor(running.name);
  return steps.length ? "Finishing up" : "Working";
};

const secs = (ms) => {
  /* Null when there is nothing to measure.
   *
   * A trace restored from a reopened chat carries its steps but no timing — the
   * duration was a live measurement and was never saved. Without this guard
   * Math.max(0, NaN) is NaN, every comparison below is false against it, and
   * the chip renders "NaN minutes". Callers show nothing rather than a figure
   * that is both wrong and alarming. */
  if (!Number.isFinite(ms)) return null;
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} second${s === 1 ? "" : "s"}`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m} minute${m === 1 ? "" : "s"}`;
};

const StatusDot = ({ status }) => {
  if (status === "running") {
    return (
      <span className="relative flex h-2 w-2 flex-none">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#A04A97] opacity-60 motion-reduce:animate-none" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#A04A97]" />
      </span>
    );
  }
  if (status === "failed") {
    /* Amber, not red.
     *
     * A tool that fails is not an emergency: the agent routinely tries another
     * route and still answers, and the reply sitting under this panel is proof
     * of that. Red is the colour of "this went wrong for you" and it belongs to
     * a failed TURN — spending it on a step the user was never harmed by makes
     * a good answer look like a broken one. */
    return <span className="h-2 w-2 flex-none rounded-full bg-[#B7791F]" />;
  }
  return <span className="h-2 w-2 flex-none rounded-full bg-[#2DAD4E]" />;
};

/* What the agent said it was doing, in its own words.
 *
 * This leads the panel. `labelFor` below is ours; this is the model's, and its
 * account of its own intent beats any label we could write. Rendered as plain
 * text on a rule rather than as markdown: it is working-out, and giving it
 * headings and bold would make it compete with the answer.
 */
const Narration = ({ text }) => (
  <div className="border-l-2 border-[#A04A97]/25 pl-3">
    {text
      .split(/\n{2,}/)
      .map((para) => para.trim())
      .filter(Boolean)
      .map((para, i) => (
        <p
          key={i}
          className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-[#6B6472] [&:not(:first-child)]:mt-2"
        >
          {para}
        </p>
      ))}
  </div>
);

const StepList = ({ steps, thought }) => (
  <div className="space-y-2">
    {thought && <Narration text={thought} />}
    {steps.length > 0 && (
      <div className="space-y-1.5">
        {steps.map((s) => (
          <div key={s.id} className="flex items-start gap-2.5">
            <span className="mt-[6px]">
              <StatusDot status={s.status} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="text-[13.5px] font-medium text-[#3F3F46]">{labelFor(s.name)}</span>
              {s.detail && (
                <span className="ml-1.5 text-[13px] text-[#77707E]">{s.detail}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* Live panel — shown while the turn is running.
 *
 * Two states, and the difference is whether the server has told us anything:
 *   nothing reported yet -> a generic working card, no step names, no timer
 *   a real step arrived  -> the panel, with the named steps and elapsed time
 */
export const CopilotReasoningLive = ({ trace }) => {
  const [now, setNow] = useState(() => Date.now());
  const startedAt = trace?.startedAt;
  const steps = trace?.steps || [];
  const hasActivity = steps.length > 0 || !!trace?.thought;

  // One shared tick, and only while there is a panel with a clock on it.
  useEffect(() => {
    if (!startedAt || !hasActivity) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt, hasActivity]);

  if (!trace) return null;

  /* Nothing has been reported yet.
   *
   * The label is deliberately generic. At this moment the only true statements
   * are "the question was sent" and "no answer yet" — naming a step here would
   * be a trace we invented, and the panel below takes over with the real work
   * the instant a frame arrives. So: presence and motion, no claims.
   *
   * The bar is indeterminate for the same reason. A percentage would imply a
   * proportion nothing has measured. */
  if (!hasActivity) {
    return (
      <div className="flex items-center gap-3 animate-copilot-rise motion-reduce:animate-none">
        <CopilotAvatar />
        <div className="relative min-w-0 overflow-hidden rounded-xl border border-[#18101C]/[0.06] bg-white/90 pl-3.5 pr-4 py-2.5 shadow-[0_1px_2px_rgba(24,16,28,0.04),0_10px_24px_-18px_rgba(24,16,28,0.4)] backdrop-blur-[6px]">
          <span className="bg-gradient-to-r from-[#6B6472] via-[#A04A97] to-[#6B6472] bg-[length:200%_100%] bg-clip-text text-[13.5px] font-medium text-transparent animate-copilot-shimmer motion-reduce:animate-none motion-reduce:text-[#6B6472]">
            Thinking…
          </span>

          {/* Sits on the box's own bottom edge, so it reads as the card
              working rather than as a separate widget under it. */}
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] overflow-hidden">
            <span className="block h-full w-1/4 rounded-full bg-gradient-to-r from-transparent via-[#A04A97] to-transparent animate-copilot-sweep motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-40" />
          </span>
        </div>
      </div>
    );
  }

  const elapsed = startedAt ? now - startedAt : 0;

  /* The live step leads; the finished ones sit under it.
   *
   * The header used to name the running step AND the list repeated it directly
   * below, so the most common case — one tool, mid-flight — printed the same
   * sentence twice, stacked. The header now owns the current step and the list
   * owns only what is already done, which is the difference between a status
   * and a log. */
  const running = [...steps].reverse().find((st) => st.status === "running");
  const finished = steps.filter((st) => st.status !== "running");

  return (
    <div className="flex gap-3 animate-copilot-rise motion-reduce:animate-none">
      <CopilotAvatar />

      <div className="relative min-w-0 flex-1 overflow-hidden rounded-xl border border-[#18101C]/[0.06] bg-gradient-to-b from-white to-[#FBF9FB] px-4 py-3 shadow-[0_1px_2px_rgba(24,16,28,0.04),0_10px_24px_-18px_rgba(24,16,28,0.4)]">
        <div className="flex items-baseline gap-2">
          <span className="bg-gradient-to-r from-[#6B6472] via-[#A04A97] to-[#6B6472] bg-[length:200%_100%] bg-clip-text text-[14px] font-semibold text-transparent animate-copilot-shimmer motion-reduce:animate-none motion-reduce:text-[#6B6472]">
            {headlineFor(steps)}
          </span>
          <span className="text-[12.5px] tabular-nums text-[#77707E]">{secs(elapsed)}</span>
        </div>

        {/* The one thing the header cannot say: what it is being run against. */}
        {running?.detail && (
          <p className="mt-1 truncate text-[13px] text-[#6B6472]" title={running.detail}>
            {running.detail}
          </p>
        )}

        {trace.thought && (
          <div className="mt-2.5">
            <Narration text={trace.thought} />
          </div>
        )}

        {finished.length > 0 && (
          <ul className="mt-2.5 space-y-1.5 border-t border-[#18101C]/[0.06] pt-2.5">
            {finished.map((st) => (
              <li key={st.id} className="flex items-start gap-2">
                <span className="mt-[3px] flex-none">
                  <StatusDot status={st.status} />
                </span>
                <span className="min-w-0 flex-1 text-[13px] leading-snug">
                  <span className={st.status === "failed" ? "text-[#92620B]" : "text-[#6B6472]"}>
                    {doneLabelFor(st.name)}
                  </span>
                  {st.detail && <span className="ml-1.5 text-[#77707E]">{st.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Same indeterminate sweep as the opening card, so the two states read
            as one thing changing rather than two components swapping. */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] overflow-hidden">
          <span className="block h-full w-1/4 rounded-full bg-gradient-to-r from-transparent via-[#A04A97] to-transparent animate-copilot-sweep motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-40" />
        </span>
      </div>
    </div>
  );
};

/* Collapsed summary — attached above the reply it produced. */
export const CopilotReasoningSummary = ({ trace }) => {
  const [open, setOpen] = useState(false);

  if (!trace) return null;
  const steps = trace.steps || [];
  // A chip that expands to nothing is worse than no chip. If the agent answered
  // directly, there is no work to show and the elapsed time on its own tells the
  // user nothing they can act on.
  if (!steps.length && !trace.thought) return null;

  const count = steps.length;
  const failed = steps.filter((s) => s.status === "failed").length;

  // One step is worth naming; several are worth counting. Only when there were
  // no steps at all does the elapsed time stand alone — and then "thought" is
  // literally all that happened.
  const summary =
    count === 1
      ? doneLabelFor(steps[0].name)
      : count > 1
      ? `${count} steps`
      : `Thought for ${secs(trace.elapsedMs) || "a moment"}`;
  // Only when it was actually measured — a restored trace has steps but no clock.
  const elapsed = secs(trace.elapsedMs);
  const showElapsed = count > 0 && !!elapsed;

  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 -ml-1.5 text-[12.5px] text-[#77707E] transition-colors duration-200 hover:bg-[#18101C]/[0.04] hover:text-[#52525B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
      >
        <svg aria-hidden="true"
          width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform duration-200 ${open ? "rotate-90" : ""} motion-reduce:transition-none`}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        {summary}
        {showElapsed && ` · ${elapsed}`}
        {failed > 0 && (
          /* Reads as a footnote on an answer that arrived, because that is what
             it is — the turn succeeded or this chip would not exist. */
          <span className="text-[#92620B]">
            {` · ${failed} retried`}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-1.5 rounded-xl border border-[#18101C]/[0.06] bg-[#FBF9FB] px-3.5 py-2.5 animate-copilot-rise motion-reduce:animate-none">
          <StepList steps={steps} thought={trace.thought} />
        </div>
      )}
    </div>
  );
};

export default CopilotReasoningLive;
